// Клиентский BFF-слой домена `communications` (client-communications).
// Источник истины: `contract.md` §C.3 (8 функций), §A.1..§A.8 (backend-API),
// §B.1..§B.8 (BFF route handlers), §A.2.4/§A.3.4 (RATE_LIMITED + Retry-After).
// Parity `accounting-calendar-client.ts`/`profile-client.ts`/`auth-client.ts`.
//
// Все функции — own-origin `fetch('/api/communications/<method>')` (BFF-нога):
//   • `credentials: 'same-origin'` — браузер прикрепляет httpOnly cookie (access-JWT);
//   • **НЕ** выставляют `Authorization`-заголовок (AGENTS.md, ARCH-D-028);
//   • клиентский JS токен не читает.
// Успех → распарсенный JSON (развёрнутый, без обёртки `{success}`); ошибка →
// `PortalApiError` (коды/fields/extras из route handler).
//
// RATE_LIMITED (parity auth.cs.md §10): BFF ставит HTTP 429 + заголовок
// `Retry-After: <retry_after>` (contract §B.1 catch-расширение). Клиентский слой
// читает заголовок и прокидывает значение в `PortalApiError.extras.retry_after`,
// чтобы `ResendCodeButton`/UX могли показать countdown (AC-CMP-008.1, server
// authoritative; fallback 30s на client).
//
// Refresh-on-401 вшит в `clientFetch` (fix-auth-session-lifetime) — НЕ дублируется.

import type {
  AddEmailConnectionRequest,
  ListConnectionsResponse,
  ListMessagesResponse,
  MarkAllReadResponse,
  MarkReadResponse,
  SimpleMessageResponse,
  UpdateCommunicationSettingRequest,
  UpdateConnectionRequest,
} from '@/lib/api/types';
import { PortalApiError } from './errors';
import { clientFetch } from './client-fetch';

interface ErrorResponseBody {
  success: false;
  error: {
    code: string;
    message: string;
    fields?: Record<string, string>;
    /** LAST_PATH (контракт §A.6.4): массив затронутых типов (BFF пробрасывает
     *  из `err.extras` в body — additive к §B.1 catch-блоку). */
    blocked_types?: string[];
  };
}

/**
 * Парсит BFF-ответ; на `success:false` бросает `PortalApiError`.
 *
 * На `RATE_LIMITED` (HTTP 429) — читает заголовок `Retry-After` и прокидывает
 * его в `PortalApiError.extras.retry_after` (int | undefined). Заголовок ставит
 * route handler'а catch-расширение contract §B.1 (mirror из `err.extras.retry_after`).
 * `clientFetch` НЕ видит заголовок — здесь отдельная логика, parity
 * `auth-client.ts` (там `retry_after` читается из body; здесь — из заголовка,
 * т.к. communications-routes прокидывают `extras` только в header, не в body).
 *
 * `retry_after` нормализуется в `number`: парсится как int, невалидное/отсутствующее
 * → `undefined` (caller применит дефолт 30s).
 */
async function parseCommsResponse<T extends { success: true }>(
  res: Response,
): Promise<T> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    // malformed/non-JSON (серверная аномалия, напр. 502 от прокси) — обезличенный INTERNAL_ERROR.
    throw new PortalApiError(
      'INTERNAL_ERROR',
      res.status || 500,
      'Внутренняя ошибка',
    );
  }

  if (
    body &&
    typeof body === 'object' &&
    (body as { success?: unknown }).success === true
  ) {
    return body as T;
  }

  const errBody = body as ErrorResponseBody | undefined;
  const code = errBody?.error?.code ?? 'INTERNAL_ERROR';
  const message = errBody?.error?.message ?? 'Внутренняя ошибка';
  const fields = errBody?.error?.fields;

  // RATE_LIMITED → читаем `Retry-After` заголовок (server authoritative).
  let extras: Record<string, unknown> | undefined;
  if (code === 'RATE_LIMITED') {
    const retryAfterHeader = res.headers.get('retry-after');
    if (retryAfterHeader !== null) {
      const parsed = Number.parseInt(retryAfterHeader, 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        extras = { retry_after: parsed };
      }
    }
  }

  // LAST_PATH → пробрасываем `blocked_types` из body в `extras` (контракт §A.6.4;
  // BFF route `update-communication-setting` кладёт массив в `error.blocked_types`).
  // UI (ChecklistRow) персонализирует toast и rollback Switch в `true`.
  if (
    code === 'LAST_PATH' &&
    errBody?.error?.blocked_types &&
    Array.isArray(errBody.error.blocked_types)
  ) {
    extras = {
      ...(extras ?? {}),
      blocked_types: errBody.error.blocked_types.filter(
        (v): v is string => typeof v === 'string',
      ),
    };
  }

  throw new PortalApiError(code, res.status || 500, message, fields, extras);
}

// =============================================================================
// Список подключений + мутации email-подключений (contract §C.3).
// =============================================================================

/**
 * `GET /api/communications/list-connections` — snapshot всех подключений профиля
 * (contract §A.1/§C.3). User-specific → no-store. Регистрационное подключение
 * идёт первым (backend сортирует, AC-CM-012.1).
 */
export async function fetchConnections(): Promise<ListConnectionsResponse> {
  const res = await clientFetch('/api/communications/list-connections', {
    credentials: 'same-origin',
    headers: { 'Cache-Control': 'no-store' },
  });
  return parseCommsResponse<ListConnectionsResponse>(res);
}

/**
 * `POST /api/communications/add-email-connection` — добавить email-подключение
 * и запустить подтверждение (contract §A.2/§C.3). Возвращает новый snapshot
 * всех подключений (с новой карточкой в состоянии «Не подтверждена»).
 * На `RATE_LIMITED` → `PortalApiError` с `extras.retry_after` (из заголовка).
 */
export async function addEmailConnection(
  body: AddEmailConnectionRequest,
): Promise<ListConnectionsResponse> {
  const res = await clientFetch('/api/communications/add-email-connection', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseCommsResponse<ListConnectionsResponse>(res);
}

/**
 * `POST /api/communications/request-verification-code` — повторная отправка кода
 * подтверждения (contract §A.3/§C.3). Anti-enumeration: обобщённый успех для
 * несуществующего/чужого (AC-CM-014.1); UI НЕ различает ветви. На `RATE_LIMITED`
 * (per-connection 1/30s или per-user 5/600s) → `extras.retry_after`.
 */
export async function requestVerificationCode(
  connection: string,
): Promise<SimpleMessageResponse> {
  const res = await clientFetch(
    '/api/communications/request-verification-code',
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ connection }),
    },
  );
  return parseCommsResponse<SimpleMessageResponse>(res);
}

/**
 * `POST /api/communications/update-connection` — обновить email-подключение
 * (contract §A.4/§C.3): label/email_address/is_active. Возвращает новый snapshot.
 * На `PROTECTED_CONNECTION` (409, регистрационная смена адреса) / `LAST_PATH`
 * (409, отключение последнего пути Helpdesk/CRM) → `PortalApiError` без extras.
 */
export async function updateConnection(
  body: UpdateConnectionRequest,
): Promise<ListConnectionsResponse> {
  const res = await clientFetch('/api/communications/update-connection', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseCommsResponse<ListConnectionsResponse>(res);
}

/**
 * `POST /api/communications/delete-connection` — удалить подключение (contract
 * §A.5/§C.3). Возвращает snapshot без удалённого. На `PROTECTED_CONNECTION`
 * (регистрационная) / `LAST_PATH` (последний путь) → 409, карточка остаётся.
 */
export async function deleteConnection(
  connection: string,
): Promise<ListConnectionsResponse> {
  const res = await clientFetch('/api/communications/delete-connection', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ connection }),
  });
  return parseCommsResponse<ListConnectionsResponse>(res);
}

// =============================================================================
// Autosave чек-листа (contract §A.6/§C.3).
// =============================================================================

/**
 * `POST /api/communications/update-communication-setting` — autosave одной строки
 * матрицы (contract §A.6/§C.3). Возвращает новый snapshot со обновлённой строкой.
 * На `LAST_PATH` (409, попытка отключить последний Helpdesk/CRM) → `fields.message_type`
 * + `extras.blocked_types` (массив затронутых типов) — UI показывает toast и
 * rollback Switch в `true` (AC-CMP-011.2).
 */
export async function updateCommunicationSetting(
  body: UpdateCommunicationSettingRequest,
): Promise<ListConnectionsResponse> {
  const res = await clientFetch(
    '/api/communications/update-communication-setting',
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  return parseCommsResponse<ListConnectionsResponse>(res);
}

// =============================================================================
// Inbox (backlog UI; API зафиксирован для будущего, contract §A.7/§A.8/§C.3).
// =============================================================================

/**
 * `GET /api/communications/list-messages[?unread_only=1]` — входящие сообщения
 * профиля (contract §A.7/§C.3; Inbox-UI — backlog). `unread_only` опционален:
 * `true` → `?unread_only=1`; иначе параметр не добавляется (backend default `"0"`).
 */
export async function fetchMessages(
  unread_only?: boolean,
): Promise<ListMessagesResponse> {
  const qs =
    unread_only === true ? `?unread_only=${encodeURIComponent('1')}` : '';
  const res = await clientFetch(`/api/communications/list-messages${qs}`, {
    credentials: 'same-origin',
    headers: { 'Cache-Control': 'no-store' },
  });
  return parseCommsResponse<ListMessagesResponse>(res);
}

/**
 * `POST /api/communications/mark-message-read` — пометить сообщение прочитанным
 * (contract §A.8/§C.3). Idempotent; возвращает обновлённый `unread_count`.
 */
export async function markMessageRead(
  message: string,
): Promise<MarkReadResponse> {
  const res = await clientFetch('/api/communications/mark-message-read', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message }),
  });
  return parseCommsResponse<MarkReadResponse>(res);
}

/**
 * `POST /api/communications/mark-all-read` — отметить все сообщения профиля
 * прочитанными (контракт inbox §B.11/§A.11; REQ-INB-005). Без body-параметров
 * (профиль — из session). Idempotent; возвращает `unread_count: 0`.
 *
 * Parity `markMessageRead` (строка 259): own-origin fetch, credentials same-origin,
 * Cache-Control no-store, error-handling через `parseCommsResponse`.
 */
export async function markAllRead(): Promise<MarkAllReadResponse> {
  const res = await clientFetch('/api/communications/mark-all-read', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}), // пустой body; Frappe игнорирует любые доп. ключи
  });
  return parseCommsResponse<MarkAllReadResponse>(res);
}
